import { listPairedDevices } from '#pplx/frontend/apps/ask/queries/notifications-queries/listPairedDevices.js';
import { getPairedDevicesQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/notificationKeys.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
// Mirrors `PairedDevice.isMentionTarget` in the iOS/macOS clients: backend
// daemon routing only targets macOS/Windows daemon devices, so any other
// paired type (Comet desktop, phones, types this build doesn't recognize)
// can't resolve as a mention and the query would silently run without
// device targeting. Excluded rather than offered as dead targets.
const MENTION_TARGET_DEVICE_TYPES = new Set([
    'personal-computer-macos',
    'personal-computer-windows',
    'personal-computer-xplat-macos',
]);
const DEVICE_TYPE_SEARCH_ALIASES = {
    'personal-computer-macos': 'mac',
    'personal-computer-windows': 'windows',
    'personal-computer-xplat-macos': 'mac',
};
// The backend marks a device online while its heartbeat is under 90s old,
// so a longer staleTime would show stale connection states in the menu.
const PAIRED_DEVICES_STALE_TIME_MS = 60 * 1000;
const STATUS_SORT_RANK = {
    online: 0,
    offline: 2,
};
const UNKNOWN_STATUS_SORT_RANK = 1;
function compareDevices(a, b) {
    const rankDiff = (STATUS_SORT_RANK[a.status] ?? UNKNOWN_STATUS_SORT_RANK) -
        (STATUS_SORT_RANK[b.status] ?? UNKNOWN_STATUS_SORT_RANK);
    if (rankDiff !== 0)
        return rankDiff;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}
function getDeviceStatusSubtitle($t, status) {
    switch (status) {
        case 'online':
            return $t({
                defaultMessage: 'Online',
                id: '7827ce52d7',
                description: 'Status subtitle for a paired device in the ask input mention menu',
            });
        case 'offline':
            return $t({
                defaultMessage: 'Offline',
                id: '8a51562160',
                description: 'Status subtitle for a paired device in the ask input mention menu',
            });
        default:
            return undefined;
    }
}
function getDeviceSearchAliases(device) {
    return [device.model, DEVICE_TYPE_SEARCH_ALIASES[device.type]].filter((alias) => !!alias);
}
export function useAtMentionDevices({ reason, isLazy = true, enabled = true, }) {
    const { $t } = useIntl();
    const [isTriggered, setIsTriggered] = useState(() => !isLazy);
    const { data, isLoading, isStale, refetch } = useQuery({
        queryKey: getPairedDevicesQueryKey(),
        queryFn: () => listPairedDevices({ reason }),
        enabled: enabled && isTriggered,
        staleTime: PAIRED_DEVICES_STALE_TIME_MS,
    });
    const onTrigger = useCallback(() => {
        if (!isTriggered) {
            setIsTriggered(true);
        }
        else if (isStale) {
            refetch();
        }
    }, [isTriggered, isStale, refetch]);
    const mentionDevicesOptions = useMemo(() => {
        const devices = data?.devices ?? [];
        return devices
            .filter((device) => MENTION_TARGET_DEVICE_TYPES.has(device.type))
            .sort(compareDevices)
            .map((device) => ({
            uuid: device.id,
            variant: 'device',
            label: device.name,
            subtitle: getDeviceStatusSubtitle($t, device.status),
            icon: {
                type: 'icon',
                // $icon() is resolved by a build-time AST scan and only accepts
                // string literals, so each branch needs its own call.
                icon: device.type === 'personal-computer-macos' ||
                    device.type === 'personal-computer-xplat-macos'
                    ? $icon('device-laptop')
                    : $icon('device-desktop'),
            },
            searchAliases: getDeviceSearchAliases(device),
        }));
    }, [data, $t]);
    return useMemo(() => ({
        isAtMentionDevicesEnabled: enabled,
        mentionDevicesOptions,
        isLoading,
        onTrigger,
    }), [enabled, isLoading, mentionDevicesOptions, onTrigger]);
}
//# sourceMappingURL=useAtMentionDevices.js.map